/**
 * Password Type Selector Component
 * 
 * Component for selecting password type (IAR or custom) and input fields.
 */

import * as React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Eye, EyeOff } from "lucide-react";

interface PasswordTypeSelectorProps {
  passwordType: "iar" | "custom";
  onPasswordTypeChange: (type: "iar" | "custom") => void;
  userIar?: string;
  customPassword: string;
  onCustomPasswordChange: (password: string) => void;
  showPassword: boolean;
  onShowPasswordToggle: () => void;
}

export function PasswordTypeSelector({
  passwordType,
  onPasswordTypeChange,
  userIar,
  customPassword,
  onCustomPasswordChange,
  showPassword,
  onShowPasswordToggle,
}: PasswordTypeSelectorProps) {
  return (
    <RadioGroup value={passwordType} onValueChange={(value) => onPasswordTypeChange(value as "iar" | "custom")}>
      <div 
        className="flex items-center space-x-2 cursor-pointer"
        onClick={(e) => {
          // Only trigger if not clicking directly on RadioGroupItem
          if ((e.target as HTMLElement).closest('[data-slot="radio-group-item"]') === null) {
            onPasswordTypeChange("iar");
          }
        }}
      >
        <RadioGroupItem value="iar" id="iar" />
        <Label 
          htmlFor="iar" 
          className="cursor-pointer flex-1 select-none"
        >
          Gunakan IAR sebagai password
        </Label>
      </div>
      {passwordType === "iar" && userIar && (
        <div className="ml-6 mt-2">
          <Label className="text-sm text-muted-foreground">IAR:</Label>
          <Input
            value={userIar}
            readOnly
            className="mt-1 bg-muted"
          />
        </div>
      )}
      {passwordType === "iar" && !userIar && (
        <div className="ml-6 mt-2 text-sm text-yellow-600 dark:text-yellow-400">
          IAR tidak tersedia untuk pengguna ini
        </div>
      )}

      <div 
        className="flex items-center space-x-2 mt-4 cursor-pointer"
        onClick={(e) => {
          // Only trigger if not clicking directly on RadioGroupItem
          if ((e.target as HTMLElement).closest('[data-slot="radio-group-item"]') === null) {
            onPasswordTypeChange("custom");
          }
        }}
      >
        <RadioGroupItem value="custom" id="custom" />
        <Label 
          htmlFor="custom" 
          className="cursor-pointer flex-1 select-none"
        >
          Masukkan password custom
        </Label>
      </div>
      {passwordType === "custom" && (
        <div className="ml-6 mt-2">
          <Label htmlFor="custom-password" className="text-sm">
            Password Baru:
          </Label>
          <div className="relative mt-1">
            <Input
              id="custom-password"
              type={showPassword ? "text" : "password"}
              value={customPassword}
              onChange={(e) => onCustomPasswordChange(e.target.value)}
              placeholder="Masukkan password baru"
              className="pr-10"
            />
            <button
              type="button"
              onClick={onShowPasswordToggle}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      )}
    </RadioGroup>
  );
}

